import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { KeyIcon, PencilIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Profile = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { get, put, loading } = useApi();
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    console.log('Fetching user profile...');
    console.log('Current user:', currentUser);
    
    if (!currentUser?.username) {
      console.log('No username found');
      setError('User not logged in');
      return;
    }
    
    try {
      // Get all users and find by username
      const { data, error } = await get('/api/users');
      
      if (error) {
        console.error('Error fetching users:', error);
        setError(error);
      } else if (data && Array.isArray(data)) {
        const found = data.find(u => u.username === currentUser.username);
        console.log('Found user:', found);
        if (found) {
          setUserData(found);
          setFormData({
            fullName: found.fullName || '',
            email: found.email || ''
          });
        } else {
          setError('User not found');
        }
      }
    } catch (err) {
      console.error('Exception:', err);
      setError(err.message);
    }
  };

  const handleEditToggle = () => {
    setEditing(!editing);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    const { error } = await put(`/api/users/${userData.userId}`, {
      fullName: formData.fullName,
      email: formData.email
    });
    
    if (!error) {
      toast.success('Profile updated successfully');
      setEditing(false);
      fetchUserProfile();
    } else {
      toast.error(error || 'Failed to update profile');
    }
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>Error: {error}</p>
          <button 
            onClick={fetchUserProfile}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!userData) {
    return (
      <div className="p-6 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-2">Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
          {!editing && (
            <button
              onClick={handleEditToggle}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
            >
              <PencilIcon className="h-5 w-5" />
              <span>Edit Profile</span>
            </button>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-500">Username</label>
            <p className="text-lg font-medium text-gray-900">{userData.username}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500">User ID</label>
            <p className="text-lg font-medium text-gray-900">{userData.userId}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500">Full Name</label>
            {editing ? (
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.fullName ? 'border-red-500' : ''}`}
              />
            ) : (
              <p className="text-lg font-medium text-gray-900">{userData.fullName || '-'}</p>
            )}
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500">Email</label>
            {editing ? (
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.email ? 'border-red-500' : ''}`}
              />
            ) : (
              <p className="text-lg font-medium text-gray-900">{userData.email || '-'}</p>
            )}
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <span className={`px-2 py-1 rounded-full text-xs ${
              userData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {userData.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            {editing ? (
              <>
                <button
                  onClick={handleEditToggle}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={handleChangePassword}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 flex items-center space-x-2"
              >
                <KeyIcon className="h-5 w-5" />
                <span>Change Password</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;